import type {
  Control,
  RaterRule,
  RaterRulesByField,
  SchemaNode,
} from './types';

// camelCase field key -> human label: "amendatoryWaitingPeriod" -> "Amendatory Waiting Period".
export function humanizeFieldKey(key: string): string {
  const spaced = key
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

function resolveLabel(fieldKey: string, node: SchemaNode): string {
  return node.title ?? humanizeFieldKey(fieldKey);
}

// An object node's child fields. Top-level coverage objects nest children under
// properties.props.properties; array-item object rows nest them directly under
// properties.<key> (minus the value/values/props wrapper keys). Handle both.
function objectChildren(node: SchemaNode): Record<string, SchemaNode> {
  const viaProps = node.properties?.props?.properties;
  if (viaProps) {
    return viaProps;
  }
  const direct = node.properties as Record<string, SchemaNode> | undefined;
  if (!direct) {
    return {};
  }
  const { value, values, props, ...rest } = direct;
  return rest;
}

function isScalarNode(node: SchemaNode): boolean {
  return (
    Boolean(node.properties?.value) &&
    Object.keys(objectChildren(node)).length === 0
  );
}

function itemControls(item: SchemaNode): Control[] {
  if (isScalarNode(item)) {
    return [{ ...toControl('value', item), label: '' }];
  }
  return schemaToControls(item);
}

// Pure schema-node -> control mapping. No FieldType enum, no Redux, no fetch.
// `rule` is this field's rater-rule overlay (when present); its `listMaxSize`
// caps a list field's row count and takes precedence over the schema's own
// `maxItems`, mirroring buildValidationSchema so the Add button and submit
// validation agree on the maximum (FPX-7029).
export function toControl(
  fieldKey: string,
  node: SchemaNode,
  rule?: RaterRule
): Control {
  const label = resolveLabel(fieldKey, node);

  const array = node.properties?.values;
  if (array) {
    const item = Array.isArray(array.items) ? array.items[0] : array.items;
    return {
      kind: 'array',
      fieldKey,
      label,
      maxItems: rule?.listMaxSize ?? array.maxItems,
      itemFields: item ? itemControls(item) : [],
      scalarItem: item ? isScalarNode(item) : false,
    };
  }

  // A non-repeating object group: children under properties.props.properties with
  // no value/values wrapper. Recurse for its fields. Nested group fields are
  // rule-less, mirroring array rows (see schemaToControls).
  const groupChildren = node.properties?.props?.properties;
  if (groupChildren && Object.keys(groupChildren).length > 0) {
    return { kind: 'group', fieldKey, label, fields: schemaToControls(node) };
  }

  const value = node.properties?.value;
  if (value?.type === 'number') {
    return {
      kind: 'number',
      fieldKey,
      label,
      minimum: value.minimum,
      maximum: value.maximum,
      ...(value.format === 'currency' && { currency: true }),
    };
  }
  if (value?.type === 'boolean') {
    return { kind: 'toggle', fieldKey, label };
  }
  if (value?.type === 'string' && value.format === 'date') {
    return { kind: 'date', fieldKey, label };
  }
  return { kind: 'text', fieldKey, label };
}

// Maps every child property of an object node to a control, preserving order.
// `rules` overlays the top-level fields by name; nested array rows are validated
// rule-less (see buildValidationSchema), so the recursion in itemControls passes
// no rules.
export function schemaToControls(
  node: SchemaNode,
  rules?: RaterRulesByField
): Control[] {
  return Object.entries(objectChildren(node)).map(([key, child]) =>
    toControl(key, child, rules?.[key])
  );
}
